"use client";

import { ChevronDown } from "lucide-react";
import { useId, useState } from "react";

export const BUILDER_FOOTER_LAYOUT_PREVIEW_KINDS = [
  "editorial",
  "columns",
  "centered",
  "compact",
  "split",
  "stacked",
  "showcase",
  "minimal",
] as const;

export type BuilderFooterLayoutPreviewKind =
  typeof BUILDER_FOOTER_LAYOUT_PREVIEW_KINDS[number];

export type BuilderFooterLayoutOption<Value extends string = string> = {
  label: string;
  preview: BuilderFooterLayoutPreviewKind;
  value: Value;
};

function FooterLayoutPreview({
  layout,
}: {
  layout: BuilderFooterLayoutPreviewKind;
}) {
  const line = "block h-1 rounded-full bg-white/45";
  const heading = "block h-1.5 rounded-full bg-[#ffb36b]";

  if (layout === "minimal") {
    return (
      <span
        aria-hidden="true"
        className="grid h-16 w-full grid-cols-[1.5fr_0.7fr_0.7fr_auto] items-center gap-2 rounded-md bg-[#15231f] p-2"
      >
        <span className="grid gap-1">
          <span className={`${heading} w-9`} />
          <span className={`${line} w-14`} />
        </span>
        <span className="grid gap-1">
          <span className={`${line} w-full`} />
          <span className={`${line} w-2/3`} />
        </span>
        <span className="grid gap-1">
          <span className={`${line} w-full`} />
          <span className={`${line} w-2/3`} />
        </span>
        <span className="flex gap-1">
          <span className="h-2 w-2 rounded-full bg-white/45" />
          <span className="h-2 w-2 rounded-full bg-white/45" />
        </span>
      </span>
    );
  }

  if (layout === "showcase") {
    return (
      <span
        aria-hidden="true"
        className="grid h-16 w-full grid-rows-[auto_1fr] gap-1.5 rounded-md bg-[#15231f] p-2"
      >
        <span className="grid justify-items-center gap-1">
          <span className={`${heading} w-10`} />
          <span className={`${line} w-20`} />
        </span>
        <span className="grid grid-cols-[0.8fr_0.8fr_1.4fr] gap-2">
          <span className="grid content-start gap-1">
            <span className={`${line} w-full`} />
            <span className={`${line} w-2/3`} />
          </span>
          <span className="grid content-start gap-1">
            <span className={`${line} w-full`} />
            <span className={`${line} w-2/3`} />
          </span>
          <span className="grid content-start gap-1 rounded bg-white/5 p-1">
            <span className={`${line} w-2/3`} />
            <span className="flex gap-1">
              <span className="h-2 w-2 rounded-full bg-white/45" />
              <span className="h-2 w-2 rounded-full bg-white/45" />
            </span>
          </span>
        </span>
      </span>
    );
  }

  if (layout === "stacked") {
    return (
      <span
        aria-hidden="true"
        className="grid h-16 w-full grid-rows-[auto_1px_1fr] gap-1.5 rounded-md bg-[#15231f] p-2"
      >
        <span className="flex items-start justify-between">
          <span className="grid gap-1">
            <span className={`${heading} w-10`} />
            <span className={`${line} w-16`} />
          </span>
          <span className="flex gap-1">
            <span className="h-2 w-2 rounded-full bg-white/45" />
            <span className="h-2 w-2 rounded-full bg-white/45" />
          </span>
        </span>
        <span className="block bg-white/20" />
        <span className="grid grid-cols-2 gap-3">
          <span className="grid content-start gap-1">
            <span className={`${line} w-full`} />
            <span className={`${line} w-1/2`} />
          </span>
          <span className="grid content-start gap-1">
            <span className={`${line} w-full`} />
            <span className={`${line} w-1/2`} />
          </span>
        </span>
      </span>
    );
  }

  if (layout === "split") {
    return (
      <span
        aria-hidden="true"
        className="grid h-16 w-full grid-cols-[1.1fr_1fr] gap-2 rounded-md bg-[#15231f] p-2"
      >
        <span className="grid content-center gap-1 rounded bg-white/5 p-1.5">
          <span className={`${heading} w-10`} />
          <span className={`${line} w-full`} />
          <span className={`${line} w-2/3`} />
        </span>
        <span className="grid grid-cols-2 grid-rows-2 gap-1.5">
          <span className="grid content-start gap-1">
            <span className={`${line} w-full`} />
            <span className={`${line} w-2/3`} />
          </span>
          <span className="grid content-start gap-1">
            <span className={`${line} w-full`} />
            <span className={`${line} w-2/3`} />
          </span>
          <span className="col-span-2 flex items-center gap-1 border-t border-white/15 pt-1">
            <span className="h-2 w-2 rounded-full bg-white/45" />
            <span className="h-2 w-2 rounded-full bg-white/45" />
          </span>
        </span>
      </span>
    );
  }

  if (layout === "centered") {
    return (
      <span
        aria-hidden="true"
        className="flex h-16 w-full flex-col items-center justify-center gap-1.5 rounded-md bg-[#15231f] p-2"
      >
        <span className={`${heading} w-8`} />
        <span className={`${line} w-14`} />
        <span className="mt-1 grid w-full grid-cols-3 gap-2">
          <span className={`${line} w-full`} />
          <span className={`${line} w-full`} />
          <span className={`${line} w-full`} />
        </span>
        <span className="mt-1 block h-px w-full bg-white/20" />
      </span>
    );
  }

  if (layout === "compact") {
    return (
      <span
        aria-hidden="true"
        className="grid h-16 w-full grid-cols-[1.5fr_1fr_1fr_auto] items-center gap-2 rounded-md bg-[#15231f] p-2"
      >
        <span className="grid gap-1">
          <span className={`${heading} w-8`} />
          <span className={`${line} w-12`} />
        </span>
        <span className="grid gap-1">
          <span className={`${line} w-full`} />
          <span className={`${line} w-2/3`} />
        </span>
        <span className="grid gap-1">
          <span className={`${line} w-full`} />
          <span className={`${line} w-2/3`} />
        </span>
        <span className="flex gap-1">
          <span className="h-2 w-2 rounded-full bg-white/45" />
          <span className="h-2 w-2 rounded-full bg-white/45" />
        </span>
      </span>
    );
  }

  if (layout === "columns") {
    return (
      <span
        aria-hidden="true"
        className="grid h-16 w-full grid-cols-4 gap-2 rounded-md bg-[#15231f] p-2"
      >
        {[0, 1, 2, 3].map((index) => (
          <span className="grid content-start gap-1" key={index}>
            <span className={`${index === 0 ? heading : line} w-full`} />
            <span className={`${line} w-full`} />
            <span className={`${line} w-2/3`} />
          </span>
        ))}
      </span>
    );
  }

  return (
    <span
      aria-hidden="true"
      className="grid h-16 w-full grid-cols-[1.6fr_0.8fr_0.8fr_1.1fr] gap-2 rounded-md bg-[#15231f] p-2"
    >
      <span className="grid content-start gap-1">
        <span className={`${heading} w-10`} />
        <span className={`${line} w-16`} />
        <span className={`${line} w-10`} />
      </span>
      <span className="grid content-start gap-1">
        <span className={`${line} w-full`} />
        <span className={`${line} w-2/3`} />
      </span>
      <span className="grid content-start gap-1">
        <span className={`${line} w-full`} />
        <span className={`${line} w-2/3`} />
      </span>
      <span className="grid content-start gap-1">
        <span className={`${line} w-full`} />
        <span className="flex gap-1">
          <span className="h-2 w-2 rounded-full bg-white/45" />
          <span className="h-2 w-2 rounded-full bg-white/45" />
        </span>
      </span>
    </span>
  );
}

export function BuilderFooterLayoutPicker<Value extends string>({
  label,
  onChange,
  options,
  value,
}: {
  label: string;
  onChange: (value: Value) => void;
  options: readonly BuilderFooterLayoutOption<Value>[];
  value: Value;
}) {
  const [open, setOpen] = useState(false);
  const listboxId = useId();
  const activeOption = options.find((option) => option.value === value)
    ?? options[0];

  if (!activeOption) {
    return null;
  }

  return (
    <div
      className="grid gap-2 py-2"
      data-builder-footer-layout-picker="true"
    >
      <span className="text-xs font-medium text-text">{label}</span>
      <button
        type="button"
        aria-controls={listboxId}
        aria-expanded={open}
        className="flex w-full items-center gap-3 rounded-lg border border-border bg-surface p-2 text-left transition hover:border-border-strong"
        onClick={() => setOpen((current) => !current)}
      >
        <span className="w-28 shrink-0">
          <FooterLayoutPreview layout={activeOption.preview} />
        </span>
        <span className="min-w-0 flex-1 text-xs font-semibold text-text">
          {activeOption.label}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={[
            "h-4 w-4 shrink-0 text-text-secondary transition-transform",
            open ? "rotate-180" : "",
          ].join(" ")}
        />
      </button>
      {open ? (
        <div
          className="grid max-h-80 grid-cols-2 gap-2 overflow-y-auto rounded-lg border border-border bg-surface p-2"
          id={listboxId}
          role="listbox"
        >
          {options.map((option) => (
            <button
              type="button"
              aria-label={option.label}
              aria-selected={activeOption.value === option.value}
              className={[
                "rounded-lg border p-1.5 text-left transition",
                activeOption.value === option.value
                  ? "border-primary bg-primary/10"
                  : "border-border bg-surface hover:border-border-strong",
              ].join(" ")}
              data-builder-footer-layout-option={option.value}
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
              role="option"
            >
              <FooterLayoutPreview layout={option.preview} />
              <span className="mt-1.5 block text-[10px] font-semibold text-text">
                {option.label}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
